import { useFormik, FormikProps } from "formik";
import { useSelector } from "react-redux";
import { RootState } from "store";
import * as Yup from "yup";

// Define Form Types
interface DescriptionItem {
  heading: string;
  subHeading: string;
  details: string;
}

interface Image {
  file?: Blob | MediaSource;
  preview: string;
  fileName?: string;
  mimetype?: string;
}

export interface MerchantProfileFormValues {
  bio: string;
  logo: Image;
  coverPhotos: Image[];
  description: DescriptionItem[];
}

const useMerchantProfileScreen = () => {
  // Fetch Redux state
  const merchantProfileDetails = useSelector(
    (state: RootState) => state.merchantForm.merchantProfileDetails || {}
  ) as MerchantProfileFormValues;

  // Define Initial Values (Handle empty Redux state properly)
  const initialValues: MerchantProfileFormValues = {
    bio: merchantProfileDetails.bio || "",
    logo: merchantProfileDetails.logo || {
      preview: "",
      file: undefined,
      fileName: undefined,
      mimetype: undefined,
    },
    coverPhotos: merchantProfileDetails.coverPhotos || [],
    description: merchantProfileDetails?.description?.length
      ? merchantProfileDetails.description
      : [], 
  };

  // Define Validation Schema using Yup
  const validationSchema = Yup.object({
    bio: Yup.string().required("Bio is required"),
    logo: Yup.object().shape({
      preview: Yup.string().required("Logo is required"),
    }),
    coverPhotos: Yup.array()
      .min(1, "At least one cover photo is required")
      .required("At least cover photos is required"),
    description: Yup.array()
      .max(3, "You can add up to 3 descriptions")
      .test(
        "all-or-none",
        "If a description is added, all fields (heading, subheading, details) are required",
        (descriptions) =>
          !descriptions?.length ||
          descriptions.every(
            (desc) => desc.heading && desc.subHeading && desc.details
          )
      ),
  });

  // Initialize Formik
  const formik: FormikProps<MerchantProfileFormValues> =
    useFormik<MerchantProfileFormValues>({
      initialValues,
      validationSchema,
      enableReinitialize: true,
      onSubmit: (values) => {
        console.log("Submitted Data:", values);
      },
    });

  return { formik };
};

export default useMerchantProfileScreen;
